import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Please fill in all required fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to send');
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-12 px-4 sm:px-6 lg:px-8 bg-black border-t-2 border-gold/30">
      <div className="container mx-auto max-w-4xl">
        {/* Logo - Centered */}
        <div className="text-center mb-0">
          <img
            src="https://customer-assets.emergentagent.com/job_elvis-one-more-time/artifacts/xbikd373_Logo.png"
            alt="Elvis One More Time Logo"
            className="mx-auto w-auto h-32 sm:h-40 object-contain"
          />
        </div>
        
        {/* Tagline */}
        <div className="text-center -mt-4 mb-8">
          <p className="text-silver text-sm sm:text-base italic tracking-wide">
            "re-live the memory...One More Time"
          </p>
        </div>
        
        {/* Contact CTAs */}
        <div className="flex flex-col sm:flex-row justify-center items-stretch gap-3 sm:gap-4 mb-8 px-2">
          {/* Phone */}
          <a
            href="tel:+447391120152"
            className="group flex flex-col items-center justify-center gap-1 bg-gradient-to-b from-black to-black/80 border border-gold/50 hover:border-gold px-5 py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
          >
            <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors mb-1">
              <Phone size={16} className="text-gold" />
            </div>
            <span className="text-gold font-semibold text-sm whitespace-nowrap">07391 120 152</span>
            <span className="text-silver/60 text-[10px] uppercase tracking-wider">Call Now</span>
          </a>
          
          {/* Email */}
          <a
            href="mailto:info@ukelvistribute.com"
            className="group flex flex-col items-center justify-center gap-1 bg-gradient-to-b from-black to-black/80 border border-gold/50 hover:border-gold px-5 py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
          >
            <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors mb-1">
              <Mail size={16} className="text-gold" />
            </div>
            <span className="text-gold font-semibold text-[12px] sm:text-sm whitespace-nowrap">info@ukelvistribute.com</span>
            <span className="text-silver/60 text-[10px] uppercase tracking-wider">Email Us</span>
          </a>
          
          {/* Location */}
          <a
            href="https://www.google.com/maps/search/?api=1&query=47+Flaxen+Walk+Warboys+PE28+2TR"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center justify-center gap-1 bg-gradient-to-b from-black to-black/80 border border-gold/50 hover:border-gold px-5 py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
          >
            <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors mb-1">
              <MapPin size={16} className="text-gold" />
            </div>
            <span className="text-gold font-semibold text-[11px] sm:text-sm whitespace-nowrap">47 Flaxen Walk, Warboys. PE28 2TR</span>
            <span className="text-silver/60 text-[10px] uppercase tracking-wider">View Map</span>
          </a>
        </div>

        {/* Contact Form */}
        <div className="max-w-xl mx-auto mb-8">
          {submitted ? (
            <div className="text-center py-6 px-4 bg-green-900/20 border border-green-500/30 rounded-xl">
              <p className="text-green-400 font-semibold">Thank you for your message!</p>
              <p className="text-silver text-sm mt-1">We'll be in touch soon.</p>
              <Button 
                onClick={() => setSubmitted(false)} 
                variant="outline" 
                className="mt-4 border-gold/30 text-gold hover:bg-gold/10"
              >
                Send Another
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <p className="text-center text-silver text-sm mb-4">Or send us a quick message</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="bg-black/50 border-gold/30 text-white placeholder:text-silver/50"
                  data-testid="contact-name"
                />
                <Input
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  className="bg-black/50 border-gold/30 text-white placeholder:text-silver/50"
                  data-testid="contact-email"
                />
              </div>
              <Input
                type="tel"
                placeholder="Phone Number (optional)"
                value={form.phone}
                onChange={(e) => setForm({...form, phone: e.target.value})}
                className="bg-black/50 border-gold/30 text-white placeholder:text-silver/50"
                data-testid="contact-phone"
              />
              <Textarea
                placeholder="Your Message *"
                value={form.message}
                onChange={(e) => setForm({...form, message: e.target.value})}
                rows={3}
                className="bg-black/50 border-gold/30 text-white placeholder:text-silver/50 resize-none"
                data-testid="contact-message"
              />
              {error && <p className="text-red-400 text-sm text-center">{error}</p>}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black hover:bg-gold/90 font-semibold"
                data-testid="contact-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                Send Message
              </Button>
            </form>
          )}
        </div>
        
        {/* Copyright */}
        <div className="text-center pt-4 border-t border-gold/20">
          <p className="text-white/70 text-sm">
            © 2026 Elvis - One More Time by Gary Jay. All rights reserved.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
